import React from "react";
import { toJS } from "mobx";
import { Observer, useObserver } from "mobx-react";
import { object, mixed, string } from "yup";

import { Grid, TextField, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import locale from "@bonliva-crm/locale";
import { ThemeType } from "@theme";

import PrimaryButton from "./buttons/PrimaryButton";
import { Text, Form, useFormStore } from "./formx";
import { regexps } from "@bonliva-crm/common";
import Autocomplete from "@material-ui/lab/Autocomplete";

type Props = {
  onSubmit: (values: FormData) => void;
  variant: "cellPhone" | "email";
  suggestions?: LabelValue[];
  renderSuggestions?: (options: any) => React.ReactNode;
};

const useStyles = makeStyles((theme: ThemeType) => ({
  root: {
    ...theme.mixins.childrenMarginBottom(16),
    padding: 16
  }
}));

export const AddRecipientForm: React.FC<Props> = (props) => {
  const store = useFormStore<FormData>({
    schema: props.variant === "cellPhone" ? cellPhoneFormSchema : emailFormSchema,
    onSubmit: async () => {
      props.onSubmit(toJS(store.values));
      store.reset();
    }
  });

  const classes = useStyles({});
  return useObserver(() => (
    <Form store={store} allowUpdates>
      <Grid container className={classes.root}>
        {/*{console.log("val", store.values.email)}*/}
        {props.variant === "email" && (
          <Grid item xs={12}>
            <Autocomplete
              freeSolo
              disableClearable
              inputValue={store.values?.email}
              onChange={(e, newValue: any) => {
                store.setValue("email", newValue.value);

                if (newValue.name) {
                  store.setValue("name", newValue.name);
                }

                if (newValue.avatarPic) {
                  store.setValue("avatarPic", newValue.avatarPic);
                }
              }}
              getOptionLabel={(opt) => opt.label}
              options={props.suggestions ?? []}
              renderOption={(option) => {
                return props.renderSuggestions ? (
                  props.renderSuggestions(option)
                ) : (
                  <Typography noWrap>{option.label}</Typography>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={locale.Email}
                  fullWidth
                  autoFocus
                  margin="normal"
                  onChange={(e) => store.setValue("email", e.target.value)}
                />
              )}
            />
            {props.suggestions?.length === 0 && (
              <Typography variant={"body2"}>{locale.noRecipients}</Typography>
            )}
          </Grid>
        )}

        {props.variant === "cellPhone" && (
          <Grid item xs={12}>
            <Text name="cellPhone" label={locale.CellPhone} />
          </Grid>
        )}

        <Grid item container xs={12} justify="flex-end">
          <Observer>
            {() => (
              <PrimaryButton
                type="submit"
                disabled={!store.canSubmit}
                onClick={store.submit}
              >
                {locale.Add}
              </PrimaryButton>
            )}
          </Observer>
        </Grid>
      </Grid>
    </Form>
  ));
};

type FormData = {
  cellPhone: string;
  email: string;
  name?: string;
  avatarPic?: string;
};

const emailFormSchema = object<FormData>({
  cellPhone: mixed().nullable(true),
  email: string().email(locale.InvalidEmail).required(locale.Email),
  name: string().notRequired(),
  avatarPic: string().notRequired()
});

const cellPhoneFormSchema = object<FormData>({
  cellPhone: string()
    .matches(regexps.phoneNumber, locale.CellPhone)
    .required(locale.CellPhone),
  email: mixed().nullable(true),
  name: string().nullable(true)
});
